import React from 'react';
import Chip from '@mui/joy/Chip';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import {formatRubles} from '../../../common/money';
import {formatUtcDateTime} from '../../../common/dateTime';
import {
    AdminExternalPurchaseInfo,
    ExternalMarketErrorKindDescription,
    ExternalPurchaseStatus,
    ExternalPurchaseStatusDescription,
    externalPurchaseStatusColor,
    ExternalTradeStateDescription,
} from '../../../models/externalMarkets';
import {toNum} from '../../bids/analytics/format';
import {ExternalPurchaseReviewBlock} from './ExternalPurchaseReviewBlock';

/**
 * Выкуп предмета у поставщика по заказу: статус, цена в USD, курс, себестоимость, план/факт, трейды поставщика,
 * ручной разбор с действиями
 */
export function ExternalPurchaseCard(props: {
    purchase?: AdminExternalPurchaseInfo;
    marketName?: string;
    /** Ключ поставщика: без него действия разбора недоступны */
    providerKey?: string;
    /** Данные заказа изменились — перечитать */
    onChanged?: () => void;
}) {
    const {purchase, marketName, providerKey, onChanged} = props;

    if (!purchase) {
        return (
            <Typography level="body-xs" color="warning">
                Выкуп у поставщика {marketName ?? ''} ещё не начат
            </Typography>
        );
    }

    const actual = purchase.actualProfitRub;
    const hasActual = actual !== undefined && actual !== null;
    const deviation = hasActual ? toNum(actual) - toNum(purchase.plannedProfitRub) : 0;

    return (
        <Sheet variant="soft" sx={{p: 1.5, borderRadius: 'sm'}}>
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="title-sm">Выкуп у поставщика {marketName ?? ''} #{purchase.id}</Typography>
                    <Chip size="sm" variant="soft" color={externalPurchaseStatusColor(purchase.status)}>
                        {ExternalPurchaseStatusDescription[purchase.status] ?? purchase.status}
                    </Chip>
                </Stack>
                {purchase.status === ExternalPurchaseStatus.Failed && purchase.errorKind !== undefined && purchase.errorKind !== null &&
                    <Typography level="body-xs" color="danger">
                        {ExternalMarketErrorKindDescription[purchase.errorKind] ?? purchase.errorKind}
                    </Typography>}
                {purchase.error && <Typography level="body-xs" color="danger">{purchase.error}</Typography>}
                {toNum(purchase.amountUsd) > 0 &&
                    <Typography level="body-xs">
                        Оплачено поставщику: ${toNum(purchase.amountUsd).toLocaleString('ru-RU', {minimumFractionDigits: 2})} по курсу {toNum(purchase.rateUsdRub).toLocaleString('ru-RU')} = {formatRubles(toNum(purchase.costRub))}
                    </Typography>}
                <Typography level="body-xs">
                    Прибыль: план {formatRubles(toNum(purchase.plannedProfitRub))}
                    {hasActual
                        ? <>, факт {formatRubles(toNum(actual))}{' '}
                            <Typography component="span" color={deviation < 0 ? 'danger' : 'success'}>
                                ({deviation > 0 ? '+' : ''}{formatRubles(deviation)})
                            </Typography></>
                        : ', факт — после выкупа'}
                </Typography>
                <Typography level="body-xs">
                    Аккаунт поставщика: {purchase.accountId ? `#${purchase.accountId}` : '—'}, бот: {purchase.botId ? `#${purchase.botId}` : '—'}
                    {purchase.attempts > 0 ? `, попыток: ${purchase.attempts}` : ''}
                </Typography>
                {(purchase.providerPurchaseIds.length > 0 || purchase.trades.length > 0) &&
                    <Typography level="body-xs">
                        Покупка у поставщика: {purchase.providerPurchaseIds.length > 0
                            ? purchase.providerPurchaseIds.join(', ')
                            : 'ещё не найдена в истории'}
                    </Typography>}
                {purchase.trades.map((t) =>
                    <Typography key={t.externalTradeId} level="body-xs">
                        Трейд {t.externalTradeId}{t.steamTradeId ? ` (Steam ${t.steamTradeId})` : ''}: {ExternalTradeStateDescription[t.state] ?? t.state}
                    </Typography>)}
                <Typography level="body-xs">
                    Создан {formatUtcDateTime(purchase.created)}, обновлён {formatUtcDateTime(purchase.updated)}
                </Typography>
                <ExternalPurchaseReviewBlock purchase={purchase} providerKey={providerKey}
                                             onChanged={onChanged ?? (() => undefined)}/>
            </Stack>
        </Sheet>
    );
}
